// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { Badge, Button, Group, Loader, Modal, Paper, Stack, Text, Textarea } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { createReference, normalizeErrorString } from '@medplum/core';
import type { Communication, Observation } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { RPM_OBSERVATION_CODES, RPM_OBSERVATION_SYSTEM } from '@polarissleep/shared';
import dayjs from 'dayjs';
import { type JSX, useCallback, useEffect, useState } from 'react';

import { upsertRpmObservation } from '../../utils/rpmFhir';
import {
  buildRpmNoteCommunication,
  getRpmNoteMonth,
  isRpmNoteCommunication,
  type RpmMonthNoteEntry,
  toRpmMonthNoteEntry,
} from '../../utils/rpmNotes';
import { withTenantScope } from '../../utils/tenant';

function formatMonth(month: string): string {
  return dayjs(`${month}-01`).format('MMMM YYYY');
}

function formatSent(value: string | undefined): string {
  return value ? dayjs(value).format('MMM D, YYYY [at] h:mma') : '—';
}

interface RpmMonthNotesModalProps {
  patientId: string | undefined;
  patientName?: string;
  month: string | undefined;
  opened: boolean;
  onClose: () => void;
}

function NoteBadges({ note }: { note: RpmMonthNoteEntry }): JSX.Element {
  return (
    <Group gap="xs">
      {note.asyncReviewed && (
        <Badge color="blue" variant="light">
          Async
        </Badge>
      )}
      {note.syncContact && (
        <Badge color="polaris" variant="light">
          Sync
        </Badge>
      )}
    </Group>
  );
}

export function RpmMonthNotesModal({
  patientId,
  patientName,
  month,
  opened,
  onClose,
}: RpmMonthNotesModalProps): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState<RpmMonthNoteEntry[]>([]);
  const [addendumFor, setAddendumFor] = useState<string | undefined>();
  const [addendumText, setAddendumText] = useState('');
  const [saving, setSaving] = useState(false);

  const loadNotes = useCallback(async (): Promise<RpmMonthNoteEntry[]> => {
    if (!patientId || !month) {
      return [];
    }
    setLoading(true);
    try {
      const communications = (await medplum.searchResources('Communication', {
        subject: `Patient/${patientId}`,
        _sort: '-sent',
        _count: '200',
      })) as Communication[];
      const nextNotes = communications
        .filter((communication) => isRpmNoteCommunication(communication) && getRpmNoteMonth(communication) === month)
        .map(toRpmMonthNoteEntry)
        .filter((note): note is RpmMonthNoteEntry => Boolean(note))
        .sort((a, b) => new Date(b.sent ?? 0).getTime() - new Date(a.sent ?? 0).getTime());
      setNotes(nextNotes);
      return nextNotes;
    } catch (error) {
      notifications.show({ color: 'red', message: normalizeErrorString(error) });
      return [];
    } finally {
      setLoading(false);
    }
  }, [medplum, month, patientId]);

  const syncMonthlyNote = async (remainingNotes: RpmMonthNoteEntry[]): Promise<void> => {
    if (!patientId || !month) {
      return;
    }
    const monthlyNotes = (await medplum.searchResources('Observation', {
      subject: `Patient/${patientId}`,
      code: `${RPM_OBSERVATION_SYSTEM}|${RPM_OBSERVATION_CODES.monthlyNote}`,
    })) as Observation[];
    const monthlyNote = monthlyNotes.find((observation) =>
      observation.identifier?.some(
        (identifier) => identifier.value === `Patient/${patientId}|${month}|${RPM_OBSERVATION_CODES.monthlyNote}`
      )
    );
    if (!monthlyNote) {
      return;
    }
    const latestNote = remainingNotes.find((note) => note.kind === 'note');
    if (latestNote) {
      await upsertRpmObservation(medplum, {
        patientId,
        month,
        code: RPM_OBSERVATION_CODES.monthlyNote,
        existing: monthlyNote,
        effectiveDateTime: latestNote.sent,
        value: { text: latestNote.text },
        note: latestNote.text,
      });
    } else if (monthlyNote.id) {
      await medplum.deleteResource('Observation', monthlyNote.id);
    }
  };

  useEffect(() => {
    if (!opened || !patientId || !month) {
      return;
    }

    loadNotes().catch(() => undefined);
  }, [loadNotes, month, opened, patientId]);

  const handleAddendum = async (note: RpmMonthNoteEntry): Promise<void> => {
    if (!patientId || !month || !addendumText.trim()) {
      return;
    }
    setSaving(true);
    try {
      const addendum = buildRpmNoteCommunication({
        patientId,
        patientName: patientName ?? 'Patient',
        month,
        sent: new Date().toISOString(),
        sender: profile ? createReference(profile) : undefined,
        noteText: addendumText.trim(),
        asyncReviewed: false,
        syncContact: false,
        kind: 'addendum',
        parentId: note.id,
      });
      await medplum.createResource(withTenantScope(addendum));
      setAddendumFor(undefined);
      setAddendumText('');
      await loadNotes();
      notifications.show({ color: 'green', message: 'RPM note addendum saved' });
    } catch (error) {
      notifications.show({ color: 'red', message: normalizeErrorString(error) });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (note: RpmMonthNoteEntry): Promise<void> => {
    if (!window.confirm('Delete this RPM note? This cannot be undone.')) {
      return;
    }
    setSaving(true);
    try {
      await medplum.deleteResource('Communication', note.id);
      if (note.kind === 'note') {
        await Promise.all(
          notes
            .filter((candidate) => candidate.parentId === note.id)
            .map((addendum) => medplum.deleteResource('Communication', addendum.id))
        );
      }
      if (addendumFor === note.id) {
        setAddendumFor(undefined);
        setAddendumText('');
      }
      const remainingNotes = await loadNotes();
      await syncMonthlyNote(remainingNotes);
      notifications.show({ color: 'green', message: 'RPM note deleted' });
    } catch (error) {
      notifications.show({ color: 'red', message: normalizeErrorString(error) });
    } finally {
      setSaving(false);
    }
  };

  const title = month ? `RPM Notes - ${formatMonth(month)}` : 'RPM Notes';
  let content: JSX.Element;
  if (loading) {
    content = (
      <Stack align="center" py="md">
        <Loader size="sm" />
      </Stack>
    );
  } else if (notes.length === 0) {
    content = (
      <Text c="dimmed" ta="center" py="md">
        No RPM notes recorded for this month.
      </Text>
    );
  } else {
    content = (
      <>
        {notes.map((note) => (
          <Paper key={note.id} withBorder radius="md" p="md">
            <Stack gap="xs">
              <Group justify="space-between" align="flex-start" wrap="wrap">
                <Group gap="xs">
                  {note.kind === 'addendum' && <Badge color="orange">Addendum</Badge>}
                  <NoteBadges note={note} />
                </Group>
                <Text size="sm" c="dimmed">
                  {formatSent(note.sent)}
                </Text>
              </Group>
              <Text style={{ whiteSpace: 'pre-wrap' }}>{note.text}</Text>
              <Text size="sm" c="dimmed">
                By {note.author}
              </Text>
              <Group justify="flex-end" gap="xs">
                {note.kind === 'note' && (
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    onClick={() => {
                      setAddendumFor(note.id);
                      setAddendumText('');
                    }}
                  >
                    Add addendum
                  </Button>
                )}
                <Button
                  color="red"
                  variant="subtle"
                  size="compact-sm"
                  loading={saving}
                  onClick={() => handleDelete(note)}
                >
                  Delete
                </Button>
              </Group>
              {addendumFor === note.id && (
                <Stack gap="xs">
                  <Textarea
                    aria-label="RPM note addendum"
                    placeholder="Add an addendum..."
                    value={addendumText}
                    onChange={(event) => setAddendumText(event.currentTarget.value)}
                    minRows={3}
                  />
                  <Group justify="flex-end">
                    <Button variant="default" size="compact-sm" onClick={() => setAddendumFor(undefined)}>
                      Cancel
                    </Button>
                    <Button
                      size="compact-sm"
                      loading={saving}
                      disabled={!addendumText.trim()}
                      onClick={() => handleAddendum(note).catch(() => undefined)}
                    >
                      Save addendum
                    </Button>
                  </Group>
                </Stack>
              )}
            </Stack>
          </Paper>
        ))}
      </>
    );
  }

  return (
    <Modal opened={opened} onClose={onClose} title={title} size="lg" centered>
      <Stack gap="md">
        {(patientName || month) && (
          <Text size="sm" c="dimmed">
            {patientName ? `${patientName} · ` : ''}
            {month ? formatMonth(month) : ''}
          </Text>
        )}
        {content}
      </Stack>
    </Modal>
  );
}
